/**
 * @file lightingCapabilities.ts
 * @description GCOME_AP 灯光协议适配能力（sendRead/sendWrite 签名与 shared 模块兼容）。
 *
 * GCOME_AP 灯效配置存放在功能变量区（GetFunc/SetFunc，CMD=0x05/0x06），
 * 与 HID EEPROM 内存读写协议不同。本文件提供与 shared/feature-impls/keyboard-lighting
 * 中 SendRead / SendWrite 类型签名兼容的适配函数：
 *   - sendRead(device, address, length, parser, tags)：address[0] 为功能变量区偏移，
 *     读取该偏移起 length 字节并交给 parser；
 *   - sendWrite(device, address, data)：address[0] 为功能变量区偏移，读-改-写
 *     （只覆盖目标偏移，写回发生变化的帧，避免破坏报告率/性能掩码/电量等字段）。
 *
 * 领域地址解析：addressResolver.getBaseAddr 返回 `[功能变量区偏移]`。
 */

import type { BaseDevice } from '../../../runtime/BaseDevice'
import type { CommonAddress } from '../../../types/protocol'
import { GCOME_AP_CMD, GCOME_AP_REPORT } from '../protocols/cmd'
import { calculateGcomeApAddress } from '../protocols/address'
import { FUNC_BLOCK_SIZE, KEY_MATRIX_SIZE, LED_DEFINE_SIZE } from '../protocols/lighting'

// ==================== 基础读写 ====================

/** 读取当前选中的板载配置分组（GetBase 偏移 0）。 */
export async function getActiveProfile(device: BaseDevice): Promise<number> {
  const gcome = device as unknown as GcomeApSendDevice
  const res = await gcome.sendRawApCommand({
    command: GCOME_AP_CMD.getBase,
    data: new Array(GCOME_AP_REPORT.payloadSize).fill(0),
  })
  return res.response.data[0] ?? 0
}

/** 分帧读取当前板载完整功能变量区（64 字节，地址叠加 profile 偏移）。 */
export async function readFuncBlock(device: BaseDevice, profile?: number): Promise<Uint8Array> {
  const gcome = device as unknown as GcomeApSendDevice
  const activeProfile = profile ?? (await getActiveProfile(device))
  const base = calculateGcomeApAddress(GCOME_AP_CMD.getFunc, activeProfile)
  const chunks: Uint8Array[] = []

  for (let offset = 0; offset < FUNC_BLOCK_SIZE; offset += GCOME_AP_REPORT.payloadSize) {
    const frameLen = Math.min(GCOME_AP_REPORT.payloadSize, FUNC_BLOCK_SIZE - offset)
    const res = await gcome.sendRawApCommand({
      command: GCOME_AP_CMD.getFunc,
      address: base + offset,
      data: new Array(frameLen).fill(0),
    })
    chunks.push(res.response.data)
    if (res.response.data.length < frameLen) break
  }

  return mergeChunks(chunks, FUNC_BLOCK_SIZE)
}

/** 读-改-写功能变量区，只写回发生变化的帧。 */
export async function mutateFuncBlock(
  device: BaseDevice,
  mutator: (block: Uint8Array) => void | Promise<void>,
): Promise<void> {
  const gcome = device as unknown as GcomeApSendDevice
  const profile = await getActiveProfile(device)
  const base = calculateGcomeApAddress(GCOME_AP_CMD.setFunc, profile)
  const before = await readFuncBlock(device, profile)
  const after = before.slice()
  await mutator(after)

  for (let offset = 0; offset < FUNC_BLOCK_SIZE; offset += GCOME_AP_REPORT.payloadSize) {
    const frameLen = Math.min(GCOME_AP_REPORT.payloadSize, FUNC_BLOCK_SIZE - offset)
    const beforeFrame = before.slice(offset, offset + frameLen)
    const afterFrame = after.slice(offset, offset + frameLen)
    if (bytesEqual(beforeFrame, afterFrame)) continue

    await gcome.sendRawApCommand({
      command: GCOME_AP_CMD.setFunc,
      address: base + offset,
      data: Array.from(afterFrame),
    })
  }
}

/** 分帧读取当前板载的默认按键矩阵定义区（GetDefaultKeyMatrix，每键 3 字节 Type/Code）。 */
export async function readKeyMatrix(device: BaseDevice): Promise<Uint8Array> {
  const gcome = device as unknown as GcomeApSendDevice
  const profile = await getActiveProfile(device)
  const base = calculateGcomeApAddress(GCOME_AP_CMD.getDefaultKeyMatrix, profile)
  const chunks: Uint8Array[] = []
  const maxFrames = Math.ceil(KEY_MATRIX_SIZE / GCOME_AP_REPORT.payloadSize)

  for (let i = 0; i < maxFrames; i++) {
    const address = base + i * GCOME_AP_REPORT.payloadSize
    const res = await gcome.sendRawApCommand({
      command: GCOME_AP_CMD.getDefaultKeyMatrix,
      address,
      data: new Array(GCOME_AP_REPORT.payloadSize).fill(0),
    })
    const chunk = res.response.data
    chunks.push(chunk)

    if (chunk.every((b: number) => b === 0xff || b === 0x00)) break
    if (chunk.length < GCOME_AP_REPORT.payloadSize) break
  }

  return mergeChunks(chunks, KEY_MATRIX_SIZE)
}

// ==================== shared 兼容的 sendRead / sendWrite ====================

/**
 * 领域 sendRead：address[0] = 功能变量区偏移，读取 length 字节。
 * 签名与 shared/feature-impls/keyboard-lighting 的 SendRead 兼容。
 */
export async function gcomeSendRead<T>(
  device: BaseDevice,
  address: CommonAddress,
  length: number,
  parser: (data: DataView) => T,
  _tags?: string[],
): Promise<T> {
  const block = await readFuncBlock(device)
  const offset = address[0] ?? 0
  const payload = block.slice(offset, offset + length)
  // 与 CH585M createSendRead 语义对齐：DataView 前 4 字节为报告头（此处填 0 占位），
  // 数据从偏移 4 起，shared 的 parser 通过 getUint8(4 + i) 读取真实字段。
  const viewData = new Uint8Array(length + 4)
  viewData.set(payload, 4)
  return parser(new DataView(viewData.buffer))
}

/**
 * 领域 sendWrite：address[0] = 功能变量区偏移，读-改-写（覆盖目标偏移）。
 * 根据目标偏移自动设置"灯光控制模块"（背光区→0，侧灯区→1），与网页版抓包一致。
 * 签名与 shared/feature-impls/keyboard-lighting 的 SendWrite 兼容。
 */
export async function gcomeSendWrite(
  device: BaseDevice,
  address: CommonAddress,
  data: number[],
): Promise<void> {
  const offset = address[0] ?? 0
  await mutateFuncBlock(device, (block) => {
    // 背光配置区（偏移 8-16）→ 模块=背光；侧灯配置区（偏移 24-31）→ 模块=侧灯
    if (offset >= 8 && offset <= 16) {
      block[23] = 0 // 背光
    } else if (offset >= 24 && offset <= 31) {
      block[23] = 1 // 侧灯
    }
    block.set(data, offset)
  })
}

/** 分帧写回当前板载的自定义灯光定义区（SetLedDefine）。
 *  传入 previousState（修改前的 LED 状态）做差异比较，只写发生变化的帧（包括全 0 帧），
 *  保证"熄灭/清空"（目标全 0）也能正确写入（全 0 帧不可跳过）。
 *  previousState 缺省时自动读取固件当前状态。 */
export async function writeLedDefine(
  device: BaseDevice,
  data: Uint8Array,
  previousState?: Uint8Array,
): Promise<void> {
  const gcome = device as unknown as GcomeApSendDevice
  const profile = await getActiveProfile(device)
  const base = calculateGcomeApAddress(GCOME_AP_CMD.setLedDefine, profile)
  const totalFrames = Math.ceil(data.length / GCOME_AP_REPORT.payloadSize)

  // 复用调用方已读取的 LED 状态，避免重复分帧读取
  const current = previousState ?? (await readLedDefine(device))

  for (let i = 0; i < totalFrames; i++) {
    const start = i * GCOME_AP_REPORT.payloadSize
    const end = Math.min(start + GCOME_AP_REPORT.payloadSize, data.length)
    const frameData = data.slice(start, end)
    const curFrame = current.slice(start, end)
    if (bytesEqual(frameData, curFrame)) continue

    await gcome.sendRawApCommand({
      command: GCOME_AP_CMD.setLedDefine,
      address: base + start,
      data: Array.from(frameData),
    })
  }
}

/** 分帧读取当前板载的自定义灯光定义区（GetLedDefine）。
 *  固定读满 512 字节（RGB 全 0 是合法的"熄灭"状态，不能提前中断）。 */
export async function readLedDefine(device: BaseDevice): Promise<Uint8Array> {
  const gcome = device as unknown as GcomeApSendDevice
  const profile = await getActiveProfile(device)
  const base = calculateGcomeApAddress(GCOME_AP_CMD.getLedDefine, profile)
  const chunks: Uint8Array[] = []
  const maxFrames = Math.ceil(LED_DEFINE_SIZE / GCOME_AP_REPORT.payloadSize)

  for (let i = 0; i < maxFrames; i++) {
    const address = base + i * GCOME_AP_REPORT.payloadSize
    const res = await gcome.sendRawApCommand({
      command: GCOME_AP_CMD.getLedDefine,
      address,
      data: new Array(GCOME_AP_REPORT.payloadSize).fill(0),
    })
    const chunk = res.response.data
    chunks.push(chunk)

    if (chunk.length < GCOME_AP_REPORT.payloadSize) break
  }

  return mergeChunks(chunks, LED_DEFINE_SIZE)
}

// ==================== 工具 ====================

/** 将分帧读取的块合并为定长数组，不足部分以 0 补齐。 */
export function mergeChunks(chunks: Uint8Array[], totalSize: number): Uint8Array {
  const result = new Uint8Array(totalSize)
  let offset = 0
  for (const chunk of chunks) {
    if (offset >= totalSize) break
    const len = Math.min(chunk.length, totalSize - offset)
    result.set(chunk.slice(0, len), offset)
    offset += len
  }
  return result
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/** GCOME_AP 设备发送 AP 命令的最小接口。 */
interface GcomeApSendDevice {
  sendRawApCommand(request: {
    command: number
    address?: number
    data?: number[] | Uint8Array
  }): Promise<{ response: { data: Uint8Array } }>
}
